import Type from "components/atoms/text/labels/Type";
import InventoryPresenter from "./InventoryPresenter";
import {
  GetReportInventoryCameraList,
  GetReportInvetoryRecorderList,
  ReportInventoryCameraRow,
  ReportInventoryRecorderRow,
  ReportInventoryRecorderRowDetail,
} from "api/interfaces/reportInterface.interface";
import { ReportDataRow } from "api/interfaces/reportInterface.interface";
import { useCallback, useMemo, useState } from "react";
import * as mediaQuery from "components/MediaQuery";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { useQuery } from "react-query";
import {
  getInventoryCamera,
  getInventoryRecorder,
  getReportInfo,
} from "api/reportAPI";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import {
  convertType,
  formatBytesFromMB,
  formatNumber,
  toTrim,
} from "utils/functions";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { excelDateFormat, getAccountTimeFormat } from "utils/timeUtil";
import { useParams } from "react-router-dom";
import RecorderTitle from "components/blocks/recorder/RecorderTitle";
import { convertMacAddrFormat, recorderTitleString } from "utils/RecorderUtil";

import { TableColumnsType } from "antd";
import { ColumnType } from "antd/es/table";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { useMenu } from "hook/useMenu";
import NoAvailable from "components/templates/nopage/NoAvailable";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import InventoryExpandList from "./InventoryExpandList";
import { ExpandableConfig } from "antd/es/table/interface";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import useAccountAccess from "hook/useAccountAccess";

type Props = {
  state: ReportDataRow | undefined;
};

export enum INVENTORY_MENU {
  RECORDER = "Recorders",
  CAMERA = "Cameras",
}

export interface TotalCountInterface {
  [key: string]: number;
}

const initialRecorderQueryinfo = {
  ...baseQueryInfo,
  sortType: "installedDate",
  sortDirection: SORT_DIRECTION.DESC,
};
const initialCameraQueryinfo = {
  ...baseQueryInfo,
  sortType: "mergedSystemName",
};

const checkZeroFromSplit = (string: string, split: string) => {
  return string.split(split).every((part: string) => Number(part) === 0);
};

const formatCameraData = (
  data: ReportInventoryCameraRow[]
): ReportInventoryCameraRow[] => {
  return (data ?? []).map((camera: any) => {
    // 우선은 encoderIndex === 0 인것을 표시
    let resolution = camera.streams
      ?.filter(
        (stream: any) =>
          stream.encoderIndex === 0 &&
          !checkZeroFromSplit(stream.resolution, "x")
      )
      .map((stream: any) => toTrim(stream.resolution)).join("")

    return {
      ...camera,
      manufacturer: camera.manufacturer || "N/A",
      model: camera.model || "N/A",
      version: camera.version === "Offline" ? "N/A" : camera.version ?? "N/A",
      macAddr:
        camera.macAddr && !checkZeroFromSplit(camera.macAddr, "-")
          ? camera.macAddr.toUpperCase()
          : "N/A",
      resolution: resolution || "N/A",
      totalStorage:
        camera.storage !== undefined
          ? camera.storage?.reduce(function (prev: any, next: any) {
              return prev + next.total;
            }, 0)
          : 0,
    };
  });
};

export default function InventoryContainer(props: Props): JSX.Element {
  const params = useParams();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { menuNavigation } = useMenu({});

  const paginationPerPage = 20;
  // const navigate = useNavigate();

  const [currentMenu, setCurrentMenu] = useState<INVENTORY_MENU>(
    INVENTORY_MENU.RECORDER
  );

  const [recorderData, setRecorderData] = useState<
    ReportInventoryRecorderRow[]
  >([]);
  const [cameraData, setCameraData] = useState<ReportInventoryCameraRow[]>([]);
  const [totalDataCount, setTotalDataCount] = useState<TotalCountInterface>({
    recorder: 0,
    camera: 0,
  });
  const isMobile = mediaQuery.useIsMobile();
  const isDesktop = mediaQuery.useIsDesktop();

  const reportId = params.reportId as string;
  const [reportTitle, setReportTitle] = useState<string>("");

  const [isExportEmail, setIsExportEmail] = useState<boolean>(false);

  const onChange = (step: string) => {
    setCurrentMenu(step as INVENTORY_MENU);
  };

  const [recorderQueryInfo, setRecorderQueryInfo] = useState<BasePagination>({
    ...initialRecorderQueryinfo,
  });
  const recorderQueryKey = useQueryKey(recorderQueryInfo);

  const [cameraQueryInfo, setCameraQueryInfo] = useState<BasePagination>({
    ...initialCameraQueryinfo,
  });
  const cameraQueryKey = useQueryKey(cameraQueryInfo);
  const { AccountAccessButton, AccountAccessModal } = useAccountAccess({
    buttonType: "AccountInfoLabel",
    useTemporary: false,
    isShowOnlyUser: true,
    toPath: "/recorders/list",
  });

  const onHeaderCellRecorder = useCallback(
    (column: ColumnType<ReportInventoryRecorderRow>) => {
      return {
        onClick: () => {
          if (column.key) {
            setRecorderQueryInfo((info) => {
              return {
                ...info,
                sortType: column.key as string,
                sortDirection: onSortAntd(info.sortDirection),
                pageNum: 0,
              };
            });
          }
        },
      };
    },
    [recorderQueryInfo]
  );

  const onHeaderCellCamera = useCallback(
    (column: ColumnType<ReportInventoryCameraRow>) => {
      return {
        onClick: () => {
          if (column.key) {
            setCameraQueryInfo((info) => {
              return {
                ...info,
                sortType: column.key as string,
                sortDirection: onSortAntd(info.sortDirection),
                pageNum: 0,
              };
            });
          }
        },
      };
    },
    [setCameraQueryInfo]
  );

  // table recorderColumns column currentMenu === 0 일때
  const recorderColumns: TableColumnsType<ReportInventoryRecorderRow> = [
    {
      title: "Type",
      width: isMobile ? 70 : 110,
      key: "type",
      render: (value, row) => (
        <Type
          type={convertType(row)}
          licenseType={row.licenseType}
          isCloudCam={row.isCloudCam}
        />
      ),
    },
    {
      title: "Title",
      key: "name",
      sorter: true,
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "name"),
      onHeaderCell: onHeaderCellRecorder,
      defaultSortOrder: "descend",
      render: (value, row) => (
        <RecorderTitle
          mergedSystemName={row.mergedSystemName}
          recorderName={""}
          recorderCount={row.recorders.length}
        />
      ),
    },
    {
      title: "Organization",
      key: "accountName",
      render: (value, row) => (
        <AccountAccessButton
          accountId={row.accountId}
          accountNumber={row.accountNumber}
          accountName={row.accountName}
          level={LEVEL_TYPE.EU}
        />
      ),
      responsive: ["md"],
      hidden: selectedAccount.accountLevel === LEVEL_TYPE.EU,
      width: "10%",
    },
    {
      title: "Firmware Version",
      key: "version",
      render: (value, row) => row.firmwareVersion,
      width: 150,
      responsive:
        selectedAccount.accountLevel === LEVEL_TYPE.EU ? ["md"] : ["lg"],
    },
    {
      title: "Model",
      key: "model",
      render: (value, row) => {
        if (convertType(row) === "SPECTRUM") {
          return "Spectrum";
        }
        if (row.recorders?.length === 1) return row.recorders[0].model;
      },
      width: "7%",
      responsive:
        selectedAccount.accountLevel === LEVEL_TYPE.EU ? ["md"] : ["lg"],
    },
    {
      title: "MAC Address",
      render: (value, row) => {
        if (row.recorders?.length === 1)
          return convertMacAddrFormat(row.recorders[0].macAddr ?? "N/A");
        return convertMacAddrFormat(row.macAddr ?? "");
      },
      width: "7%",
      responsive: ["xl"],
    },
    {
      title: "Cameras",
      align: "center",
      render: (value, row) =>
        formatNumber(
          row.recorders?.length === 1
            ? row.recorders[0].cameraCount
            : row.recorders.reduce((acc, curr) => acc + curr.cameraCount, 0)
        ),
      width: 80,
    },
    // {
    //   title: "Serial No",
    //   render: (value, row) => row.serialNo || "N/A",
    // },
    {
      title: "Installer",
      render: (value, row) =>
        row.recorders?.length === 1
          ? row.recorders[0].installerName
          : row.installerName,
      responsive: ["xxl"],
      width: "10%",
    },
    {
      key: "installedDate",
      title: "Registered Date",
      sorter: true,
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "installedDate"),
      onHeaderCell: onHeaderCellRecorder,
      //BUG [RND-434] Date Format change
      render: (value, row) => {
        if (row.recorders?.length === 1) {
          return getAccountTimeFormat(
            row.recorders[0].createdAt,
            selectedAccount,
            true
          );
        }
        return getAccountTimeFormat(row.installedDate, selectedAccount, true);
      },
      responsive: ["md"],
      width: isDesktop ? 200 : "15%",
    },
    {
      title: "Location",
      sorter: true,
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "location"),
      onHeaderCell: onHeaderCellRecorder,
      key: "location",
      render: (value, row) => {
        if (row.recorders?.length === 1) {
          return row.recorders[0].location?.location ?? "Unknown";
        }
        return row.location?.location ?? "Unknown";
      },
      responsive: ["xxl"],
      width: "15%",
    },
  ];

  // table recorderColumns column currentMenu === 0 일때
  const cameraColumns: TableColumnsType<ReportInventoryCameraRow> = [
    {
      title: "Recorder",
      sorter: true,
      sortOrder: changeSortOrderAntd(cameraQueryInfo, "mergedSystemName"),
      onHeaderCell: onHeaderCellCamera,
      defaultSortOrder: "ascend",
      key: "mergedSystemName",
      render: (value, row) => (
        <RecorderTitle
          mergedSystemName={row.mergedSystemName}
          recorderName={row.recorderName}
        />
      ),
    },
    {
      title: "Camera",
      sorter: true,
      sortOrder: changeSortOrderAntd(cameraQueryInfo, "name"),
      onHeaderCell: onHeaderCellCamera,
      key: "name",
      render: (value, row) => row.name,
      width: isMobile ? 200 : "15%",
    },
    {
      title: "Organization",
      render: (value, row) => (
        <AccountAccessButton
          accountId={row.accountId}
          accountNumber={row.accountNumber}
          accountName={row.accountName}
          level={LEVEL_TYPE.EU}
        />
      ),
      hidden: selectedAccount.accountLevel === LEVEL_TYPE.EU,
      width: "15%",
    },
    {
      title: "Version",
      render: (value, row) => row.version || "N/A",
      responsive:
        selectedAccount.accountLevel === LEVEL_TYPE.EU ? undefined : ["xl"],
      width: "10%",
    },
    {
      title: "Manufacturer",
      render: (value, row) => row.manufacturer,
      responsive: ["md"],
      width: "10%",
    },
    {
      title: "Model",
      render: (value, row) => row.model,
      responsive: ["md"],
      width: "10%",
    },
    {
      title: "MAC address",
      render: (value, row) => convertMacAddrFormat(row.macAddr ?? ""),
      width: "10%",
      responsive: ["md"],
    },
    {
      title: "Storage",
      render: (value, row) => formatBytesFromMB(row.totalStorage),
      responsive: ["xxl"],
      width: 120,
    },
    {
      title: "Resolution",
      render: (value, row) => row.resolution,
      responsive: ["xxl"],
      width: 120,
    },
  ];

  const reportDetailInfo = useQuery(
    ["reportDetailInfo", reportId, selectedAccount.accountId],
    () =>
      getReportInfo({
        accountId: selectedAccount.accountId,
        reportId: reportId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.result !== undefined && res.error === 0) {
          setReportTitle(res.result.preferences?.title);
        } else {
          setReportTitle("N/A");
        }
      },
      onError: (e: any) => {
        setReportTitle("N/A");
      },
    }
  );
  const refecth = useCallback(() => {
    reportDetailInfo.refetch();
    inventoryRecorderQuery.refetch();
    inventoryCameraQuery.refetch();
  }, []);
  useStompDebouncedRefetch({
    stompKey: ["reportDetailUpdated"] as StompDateSignalKey[],
    refetch: refecth,
  });
  const excelFileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const excelFileExtension = ".xlsx";
  const excelFileName = `report-inventory-${excelDateFormat()}`;

  const RecorderKeys: string[] = useMemo(() => {
    const keys = [
      "Type",
      "Title",
      "Organization",
      "Firmware Version",
      "Model",
      "MAC Address",
      "Cameras",
      // "Serial No",
      "Installer",
      "Registered Date",
      "location",
    ];
    if (selectedAccount.accountLevel === LEVEL_TYPE.EU) {
      return keys.filter((col) => col !== "Organization");
    }
    return keys;
  }, [selectedAccount]);
  const CameraKeys: string[] = useMemo(() => {
    const keys = [
      "Recorder",
      "Camera",
      "Organization",
      "Version",
      "Manufacturer",
      "Model",
      "MAC Address",
      "Storage",
      "Resolution",
    ];
    if (selectedAccount.accountLevel === LEVEL_TYPE.EU) {
      return keys.filter((col) => col !== "Organization");
    }
    return keys;
  }, [selectedAccount]);

  const useExcelDownload = useCallback(() => {
    // console.log({matchInfoArray});
    const rs = XLSX.utils.aoa_to_sheet([RecorderKeys]);
    const ws = XLSX.utils.aoa_to_sheet([CameraKeys]);

    getInventoryRecorder({
      payload: {
        total: 0,
        pageNum: 0,
        pageLimit: totalDataCount.recorder,
        keyword: recorderQueryInfo.keyword,
        sortType: recorderQueryInfo.sortType,
        sortDirection: recorderQueryInfo.sortDirection,
      },
      reportId: reportId,
    }).then((res: any) => {
      const makeRecorderSheetRow = (
        recorder: any,
        isMerged: boolean = false
      ) => {
        let sheetArr = [
          isMerged
            ? ""
            : `${convertType(recorder)}${
                convertType(recorder).toUpperCase() === "SPECTRUM" &&
                recorder.licenseType === "enterprise" &&
                " Enterprise"
              }`,
          `${isMerged ? "└ " : ""}${recorder.name}`,
          isMerged ? "" : `${recorder.accountName} - ${recorder.accountNumber}`,
          recorder.firmwareVersion,
          recorder.model,
          convertMacAddrFormat(recorder.macAddr ?? ""),
          recorder.cameraCount,
          // data.serialNo,
          recorder.installerName,
          getAccountTimeFormat(
            recorder.installedDate ?? recorder.createdAt,
            selectedAccount,
            true
          ),
          recorder.location?.location ?? "Unknown",
        ];

        let cols = [
          { wpx: 200 },
          { wpx: 200 },
          { wpx: 200 },
          { wpx: 200 },
          { wpx: 200 },
          { wpx: 120 },
          { wpx: 100 },
          { wpx: 100 },
          { wpx: 50 },
          { wpx: 100 },
        ];

        if (selectedAccount.accountLevel === LEVEL_TYPE.EU) {
          sheetArr = sheetArr.filter((sh, index) => index !== 2);
          cols = cols.filter((col, index) => index !== 2);
        }

        XLSX.utils.sheet_add_aoa(rs, [sheetArr as any[]], { origin: -1 });
        rs["!cols"] = cols;
      };
      res.result.forEach((data: ReportInventoryRecorderRow) => {
        if (data.recorders.length > 1) {
          makeRecorderSheetRow({
            ...data,
            name: data.mergedSystemName,
            cameraCount: data.recorders.reduce(
              (acc, curr) => acc + curr.cameraCount,
              0
            ),
            model: convertType(data) === "SPECTRUM" ? "Spectrum" : data.model,
          });
        }
        data.recorders.forEach((recorder: ReportInventoryRecorderRowDetail) => {
          let model = recorder.model ?? "N/A";
          let firmwareVersion = recorder.firmwareVersion ?? "N/A";
          if (data.recorders.length > 1) {
            firmwareVersion = "";
            model = "";
          }
          makeRecorderSheetRow(
            {
              ...recorder,
              name:
                data.recorders.length === 1 && convertType(data) === "SPECTRUM"
                  ? data.mergedSystemName
                  : recorder.name,
              model: model,
              macAddr: (recorder.macAddr ?? "N/A").toUpperCase(),
              firmwareVersion: firmwareVersion,
              accountName: data.accountName,
              accountNumber: data.accountNumber,
            },
            data.recorders.length > 1
          );
        });
      });

      getInventoryCamera({
        payload: {
          total: 0,
          pageNum: 0,
          pageLimit: totalDataCount.camera,
          keyword: cameraQueryInfo.keyword,
          sortType: cameraQueryInfo.sortType,
          sortDirection: cameraQueryInfo.sortDirection,
        },
        reportId: reportId,
      }).then((res: any) => {
        formatCameraData(res.result).map((data: ReportInventoryCameraRow) => {
          let sheetArr = [
            recorderTitleString(data.recorderName, data.mergedSystemName),
            data.name,
            `${data.accountName} - ${data.accountNumber}`,
            data.version === "Offline" ? "N/A" : data.version ?? "N/A",
            data.manufacturer,
            data.model,
            convertMacAddrFormat(data.macAddr ?? ""),
            formatBytesFromMB(data.totalStorage),
            data.resolution,
          ];

          let cols = [
            { wpx: 200 },
            { wpx: 200 },
            { wpx: 200 },
            { wpx: 200 },
            { wpx: 200 },
            { wpx: 120 },
            { wpx: 100 },
            { wpx: 100 },
            { wpx: 50 },
            { wpx: 100 },
          ];
          if (selectedAccount.accountLevel === LEVEL_TYPE.EU) {
            sheetArr = sheetArr.filter((item, index) => index !== 2);
            cols = cols.filter((col, index) => index !== 2);
          }
          XLSX.utils.sheet_add_aoa(ws, [sheetArr as any[]], { origin: -1 });
          ws["!cols"] = cols;
          return false;
        });
        const wb: any = {
          Sheets: { Recorder: rs, Cameras: ws },
          SheetNames: ["Recorder", "Cameras"],
        };
        // console.log(wb);
        const excelButter = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelButter], { type: excelFileType });
        FileSaver.saveAs(excelFile, excelFileName + excelFileExtension);
      });
    });
  }, [
    reportId,
    CameraKeys,
    RecorderKeys,
    cameraQueryInfo.keyword,
    cameraQueryInfo.sortDirection,
    cameraQueryInfo.sortType,
    excelFileName,
    recorderQueryInfo.keyword,
    recorderQueryInfo.sortDirection,
    recorderQueryInfo.sortType,
    selectedAccount,
    totalDataCount.camera,
    totalDataCount.recorder,
  ]);

  const [recorderPending, setRecorderPending] = useState<boolean>(true);

  const inventoryRecorderQuery = useQuery(
    ["inventoryRecorder", recorderQueryKey, reportId],
    () =>
      getInventoryRecorder({
        payload: recorderQueryInfo,
        reportId: reportId,
      }),
    {
      retry: 0,
      enabled: reportId !== undefined,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetReportInvetoryRecorderList) => {
        if (res.result === undefined) {
          setRecorderQueryInfo({ ...initialRecorderQueryinfo });
          return;
        }
        setTotalDataCount((count) => {
          return {
            ...count,
            recorder: res.count,
          };
        });
        setRecorderData(res.result as ReportInventoryRecorderRow[]);
        setRecorderQueryInfo((info) => {
          return {
            ...info,
            ...res.page,
          };
        });
      },
      onError: (e: any) => {
        setRecorderQueryInfo({ ...initialRecorderQueryinfo });
        setRecorderData([]);
        setTotalDataCount({
          recorder: 0,
          camera: 0,
        });
      },
      onSettled: (e: any) => {
        setRecorderPending(false);
      },
    }
  );

  const [cameraPending, setCameraPending] = useState<boolean>(true);
  const inventoryCameraQuery = useQuery(
    ["inventoryCamera", cameraQueryKey, reportId],
    () =>
      getInventoryCamera({
        payload: cameraQueryInfo,
        reportId: reportId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: reportId !== undefined,
      onSuccess: (res: GetReportInventoryCameraList) => {
        if (res.result === undefined) {
          setCameraQueryInfo({ ...initialCameraQueryinfo });
          return;
        }
        setTotalDataCount((count) => {
          return {
            ...count,
            camera: res.page.total,
          };
        });

        setCameraData(formatCameraData(res.result));
        setCameraQueryInfo((info) => {
          return {
            ...info,
            ...res.page,
          };
        });
      },
      onError: (e: any) => {
        // console.error(e, "error");
        if (e.data) {
          setCameraQueryInfo({ ...initialCameraQueryinfo });
          e.data.error === 7001 && setCameraData([]);

          return;
        } else throw e;
      },
      onSettled: (e: any) => {
        setCameraPending(false);
      },
    }
  );

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      if (currentMenu === INVENTORY_MENU.RECORDER) {
        if (page !== recorderQueryInfo.pageNum + 1) {
          setRecorderQueryInfo((info) => {
            return { ...info, pageNum: (page - 1) as number };
          });
        }
      }
      if (currentMenu === INVENTORY_MENU.CAMERA) {
        if (page !== cameraQueryInfo.pageNum + 1) {
          setCameraQueryInfo((info) => {
            return { ...info, pageNum: (page - 1) as number };
          });
        }
      }
    },
    [cameraQueryInfo, recorderQueryInfo, currentMenu]
  );

  const onClickSearch = useCallback((keyword: string) => {
    setRecorderQueryInfo((query) => {
      return {
        ...query,
        pageNum: 0,
        keyword,
      };
    });
    setCameraQueryInfo((query) => {
      return {
        ...query,
        pageNum: 0,
        keyword,
      };
    });
  }, []);

  const onClickExcelEmail = useCallback(() => {
    setIsExportEmail(true);
  }, []);

  const onModalExportMail = useCallback(() => {
    setIsExportEmail(false);
  }, []);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const expandableProps = useMemo<
    ExpandableConfig<ReportInventoryRecorderRow>
  >(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys,
      onExpand: (expanded: boolean, record: ReportInventoryRecorderRow) => {
        if (expanded) {
          setExpandedRowKeys([record.mergedSystemId]);
        } else {
          setExpandedRowKeys([]);
        }
      },
      expandedRowRender: (record: ReportInventoryRecorderRow) => (
        <InventoryExpandList
          columns={
            recorderColumns as TableColumnsType<ReportInventoryRecorderRow>
          }
          data={record.recorders}
        />
      ),
      rowExpandable: (record: ReportInventoryRecorderRow) =>
        convertType(record) === "SPECTRUM" && record.recorders?.length > 1,
    };
  }, [expandedRowKeys, recorderQueryInfo]);
  // console.log(cameraData, recorderData);

  if (inventoryRecorderQuery.error) {
    return (
      <NoAvailable
        onReturn={() => menuNavigation({ to: "/reports" })}
        buttonLabel="Return to Reports"
        message="This report is no longer available"
      />
    );
  }

  return (
    <InventoryPresenter
      title={reportTitle}
      cameraColumns={cameraColumns}
      recorderColumns={recorderColumns}
      currentMenu={currentMenu}
      onChange={onChange}
      excelDownload={useExcelDownload}
      excelEmail={onClickExcelEmail}
      cameraData={cameraData}
      recorderData={recorderData}
      totalDataCount={totalDataCount}
      paginationPerPage={paginationPerPage}
      onChangePage={onChangePage}
      onClickSearch={onClickSearch}
      isExportEmail={isExportEmail}
      onModalExportMail={onModalExportMail}
      reportId={reportId}
      recorderTablePending={recorderPending}
      cameraTablePending={cameraPending}
      selectedAccount={selectedAccount}
      recorderPaginationInfo={recorderQueryInfo}
      cameraPaginationInfo={cameraQueryInfo}
      expandableProps={expandableProps}
      AccountAccessModal={AccountAccessModal}
    />
  );
}
